import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const DietitianGlenhazel = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Functional Foodie - Dietitian in Glenhazel",
    image: "https://functionalfoodie.co.za/lovable-uploads/24972437-aa8b-4772-a032-7db8f669e478.png",
    url: "https://functionalfoodie.co.za/dietitian-glenhazel",
    telephone: "+27783515631",
    address: {
      "@type": "PostalAddress",
      streetAddress: "48 Tanced Road",
      addressLocality: "Glenhazel",
      addressRegion: "Gauteng",
      postalCode: "2192",
      addressCountry: "ZA",
    },
    areaServed: [
      { "@type": "Place", name: "Glenhazel" },
      { "@type": "Place", name: "Sydenham" },
      { "@type": "Place", name: "Highlands North" },
      { "@type": "Place", name: "Linksfield" },
      { "@type": "Place", name: "Fairmount" },
      { "@type": "Place", name: "Sandringham" },
    ],
    medicalSpecialty: "Dietetics",
  };

  return (
    <div>
      <Helmet>
        <title>Dietitian in Glenhazel, Johannesburg | Functional Foodie</title>
        <meta
          name="description"
          content="Registered dietitian based in Glenhazel, Johannesburg. Personalised nutrition plans for gut health, weight management, sports nutrition and chronic disease. Medical aid rates apply."
        />
        <link rel="canonical" href="https://functionalfoodie.co.za/dietitian-glenhazel" />
        <meta property="og:title" content="Dietitian in Glenhazel, Johannesburg | Functional Foodie" />
        <meta property="og:description" content="Glenhazel-based registered dietitians offering personalised nutrition care. Book your consultation today." />
        <meta property="og:url" content="https://functionalfoodie.co.za/dietitian-glenhazel" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[70vh] bg-[url('https://images.unsplash.com/photo-1490818387583-1baba5e638af?q=80&w=1932&auto=format&fit=crop')] bg-cover bg-center">
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative container mx-auto px-4 h-full flex flex-col items-center justify-center text-center pt-20">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white">
            Dietitian in <span className="text-secondary">Glenhazel</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl">
            Your local registered dietitians in Glenhazel, serving Sydenham, Highlands North, Linksfield, Fairmount and Sandringham.
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-secondary hover:bg-secondary-light text-secondary-foreground text-lg px-8 rounded-full">
              BOOK A CONSULTATION
            </Button>
          </Link>
        </div>
      </section>

      {/* Local intro */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Your Local Dietitian in Glenhazel</h2>
          <p className="text-lg text-primary/80 leading-relaxed mb-4">
            Functional Foodie is a registered dietitian practice based at 48 Tanced Road, Glenhazel, Johannesburg. We're proud to serve the Glenhazel community and the surrounding suburbs of Sydenham, Highlands North, Linksfield, Fairmount, Sandringham and Waverley with evidence-based nutrition care.
          </p>
          <p className="text-lg text-primary/80 leading-relaxed mb-4">
            Being local means easy access — most of our Glenhazel clients are just a few minutes away. We offer in-person consultations from our rooms and virtual sessions for clients who prefer the convenience of meeting from home or the office.
          </p>
          <p className="text-lg text-primary/80 leading-relaxed">
            We understand the local community, dietary preferences and lifestyle, and we tailor every plan to fit how you actually live. Medical aid rates apply, and we work with all major South African schemes.
          </p>
        </div>
      </section>

      {/* Services for this location */}
      <section className="py-16 bg-accent/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-primary text-center mb-12">How We Can Help</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { id: "nutrition-counselling", label: "Nutrition Counselling" },
              { id: "gut-health", label: "Gut Health & IBS" },
              { id: "weight-management", label: "Weight Management" },
              { id: "sports-nutrition", label: "Sports Nutrition" },
              { id: "chronic-diseases", label: "Chronic Disease Support" },
              { id: "lifecycle-nutrition", label: "Lifecycle Nutrition" },
            ].map((s) => (
              <Link
                key={s.id}
                to={`/services/${s.id}`}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow text-primary font-semibold text-lg text-center"
              >
                {s.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary text-white text-center">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Book your Glenhazel dietitian appointment</h2>
          <p className="text-lg mb-8 text-accent">Visit us at 48 Tanced Road, Glenhazel, or book a virtual consultation.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/booking">
              <Button size="lg" className="bg-secondary hover:bg-secondary-light text-secondary-foreground text-lg px-8 rounded-full">
                BOOK ONLINE
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="text-lg px-8 rounded-full border-white text-white hover:bg-white hover:text-primary">
                CONTACT US
              </Button>
            </Link>
          </div>
          <p className="mt-8 text-sm text-accent">
            Also serving: <Link to="/dietitian-sandton" className="underline hover:text-secondary">Dietitian in Sandton</Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default DietitianGlenhazel;